import { useMemo } from 'react';
import type { Cell, Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';

type Bar = {
  label: string;
  stacks: {
    stackLabel: string;
    value: number;
  }[];
};

type UseExportOptionsProps = {
  bars: Bar[];
  comparisonBars?: Bar[];
  stackLabels: { name: string }[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const mapRows = (data: Bar[], stackLabels: { name: string }[]): Row[] => [
  [
    {},
    // Skip the last stack label ("no data available")
    ...stackLabels.slice(0, -1).map<Cell>((label) => ({
      value: label.name,
      fontWeight: 'bold',
    })),
  ],
  ...data.map<Row>((bar) => [
    {
      value: bar.label,
    },
    ...bar.stacks.map((stack) => ({
      value: stack.value,
    })),
  ]),
];

export const useExportOptions = ({
  widgetName,
  bars,
  comparisonBars,
  stackLabels,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions =>
  useMemo(() => {
    const rows = mapRows(bars, stackLabels);
    const comparisonRows = comparisonBars
      ? mapRows(comparisonBars, stackLabels)
      : undefined;

    return {
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [stackLabels, bars, comparisonBars, widgetName, queryFilters]);
